/**
 * The ``{{ root.path }}`` placeholder grammar target templates use, mirrored
 * from the SDK's ``roboto.templating.PLACEHOLDER_RE``: two braces, optional
 * whitespace, a dotted identifier, optional whitespace, two braces. Nothing
 * else (no filters, no expressions), which is what makes an in-progress token
 * cheap to detect at the caret and a completed one cheap to highlight.
 */

/** A complete placeholder, capturing the dotted name. */
const PLACEHOLDER = /\{\{\s*([A-Za-z_][A-Za-z0-9_.]*)\s*\}\}/g;

/** Characters that may continue a placeholder name. */
export const PLACEHOLDER_NAME_CHAR = /[A-Za-z0-9_.]/;

/** The ``{{name`` token the caret is inside, for autocomplete. */
export interface ActivePlaceholderToken {
  /** Offset of the opening ``{{``. Replaced from here on accept. */
  tokenStart: number;
  /** The name typed so far, up to the caret (leading whitespace dropped). */
  query: string;
  /**
   * Exclusive end of the token: past the rest of the name after the caret and
   * past a closing ``}}`` when one follows, so accepting from inside an
   * existing placeholder rewrites it instead of nesting a new one in it.
   */
  tokenEnd: number;
}

/**
 * Detect an in-progress ``{{name`` at ``cursor``. The nearest ``{{`` before
 * the caret opens the token; it is still in progress as long as the text
 * between them is optional whitespace and name characters only. Whitespace
 * after the name or any brace means the caret has left it.
 */
export function activePlaceholderTokenAtCursor(
  text: string,
  cursor: number,
): ActivePlaceholderToken | null {
  const tokenStart = text.lastIndexOf("{{", cursor - 2);
  if (tokenStart === -1) {
    return null;
  }
  const between = text.slice(tokenStart + 2, cursor);
  const match = between.match(/^\s*([A-Za-z0-9_.]*)$/);
  if (match === null) {
    return null;
  }
  let tokenEnd = cursor;
  while (
    tokenEnd < text.length &&
    PLACEHOLDER_NAME_CHAR.test(text.charAt(tokenEnd))
  ) {
    tokenEnd += 1;
  }
  const closing = text.slice(tokenEnd).match(/^\s*\}\}/);
  if (closing !== null) {
    tokenEnd += closing[0].length;
  }
  return { tokenStart, query: match[1], tokenEnd };
}

/** A complete placeholder and where it sits in the text. */
export interface PlaceholderMatch {
  /** Index of the opening ``{{``. */
  start: number;
  /** Exclusive end, past the closing ``}}``. */
  end: number;
  /** The dotted name between the braces, whitespace excluded. */
  name: string;
}

/** Every complete placeholder in ``text``, in order. */
export function findPlaceholders(text: string): PlaceholderMatch[] {
  const matches: PlaceholderMatch[] = [];
  for (const match of text.matchAll(PLACEHOLDER)) {
    matches.push({
      start: match.index,
      end: match.index + match[0].length,
      name: match[1],
    });
  }
  return matches;
}

/**
 * A run of template text with a rendering role. ``placeholder`` names a path
 * the catalog knows; ``placeholderUnknown`` one it does not, which the
 * validator will refuse at save time (or, for a root not shared by every
 * subscribed event, which resolves on some firings and not others).
 */
export interface PlaceholderSegment {
  text: string;
  kind: "plain" | "bracket" | "placeholder" | "placeholderUnknown";
}

/**
 * Split ``text`` into segments for a highlight overlay. Concatenating the
 * segments' ``text`` reproduces ``text`` exactly, including the whitespace
 * inside the braces, so an overlay built from them stays aligned with the
 * textarea it mirrors.
 */
export function segmentPlaceholders(
  text: string,
  isKnown: (name: string) => boolean,
): PlaceholderSegment[] {
  const segments: PlaceholderSegment[] = [];
  let cursor = 0;
  for (const match of findPlaceholders(text)) {
    if (match.start > cursor) {
      segments.push({ text: text.slice(cursor, match.start), kind: "plain" });
    }
    const inner = text.slice(match.start + 2, match.end - 2);
    const nameStart = inner.indexOf(match.name);
    const leading = inner.slice(0, nameStart);
    const trailing = inner.slice(nameStart + match.name.length);
    segments.push({ text: `{{${leading}`, kind: "bracket" });
    segments.push({
      text: match.name,
      kind: isKnown(match.name) ? "placeholder" : "placeholderUnknown",
    });
    segments.push({ text: `${trailing}}}`, kind: "bracket" });
    cursor = match.end;
  }
  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), kind: "plain" });
  }
  return segments;
}
